import type { ColumnDef } from '@tanstack/react-table';
import { Table } from '@shared/components';
import { formatIsoDateOnlyShort } from '@/app/utils/formatDate';
import type { PurchaseAmount, PurchaseLine } from '../types/agreements.types';
import { formatPurchaseAmount } from '../utils/formatPurchaseAmount';

interface Props {
    title: string;
    lines: PurchaseLine[];
    emptyMessage: string;
    dateColumnHeader?: string;
}

const COLUMN_WIDTHS: Record<string, number> = { startDate: 140, net: 120, gross: 120 };

/** Shared-`Table` wrapper for the services / add-ons / fees purchase-line groups.
 *  `PurchaseLine` carries no snapshot id, so rows stay index-keyed: never pass `getRowId`. */
export function PurchaseLinesTable({ title, lines, emptyMessage, dateColumnHeader }: Props) {
    const columns: ColumnDef<PurchaseLine>[] = [
        { accessorKey: 'name', header: 'Name' },
        {
            accessorKey: 'description',
            header: 'Description',
            cell: (info) => info.getValue<string | null>() ?? '—',
        },
        ...(dateColumnHeader
            ? [
                  {
                      accessorKey: 'startDate',
                      header: dateColumnHeader,
                      cell: (info: { getValue: () => string | null }) =>
                          formatIsoDateOnlyShort(info.getValue()) || '—',
                  } as ColumnDef<PurchaseLine>,
              ]
            : []),
        {
            accessorKey: 'net',
            header: 'Net',
            cell: (info) => formatPurchaseAmount(info.getValue<PurchaseAmount>()),
        },
        {
            accessorKey: 'gross',
            header: 'Gross',
            cell: (info) => formatPurchaseAmount(info.getValue<PurchaseAmount>()),
        },
    ];

    return (
        <div>
            <h3 className="mb-2 text-sm font-semibold text-mp-foreground">{title}</h3>
            <Table<PurchaseLine>
                data={lines}
                columns={columns}
                enableSorting={false}
                emptyMessage={emptyMessage}
                columnWidths={COLUMN_WIDTHS}
            />
        </div>
    );
}
